// Batch 19 · Stage C · C2 (F-152) — the public /install page: no sign-in, EN + AR, one path per device.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Languages, Share, PlusSquare, Check, Download, MonitorDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { detectDevice, isStandalone, canPromptInstall, onInstallPromptChange, promptInstall } from "@/lib/pwa";

const Step = ({ n, icon, text }) => (
  <li className="flex items-start gap-3 rounded-lg border bg-white p-3 dark:bg-slate-900" data-testid={`install-step-${n}`}>
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: "#0A3A2E", color: "#BA944A" }} aria-hidden="true">{icon}</div>
    <div className="text-sm"><span className="me-2 inline-block rounded-full bg-slate-900 px-2 text-[11px] font-semibold text-white">{n}</span>{text}</div>
  </li>
);

export default function Install() {
  const { t, lang, toggle } = useI18n();
  const [device] = useState(() => detectDevice(typeof navigator !== "undefined" ? navigator.userAgent : "", { maxTouchPoints: typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0 }));
  const [installed, setInstalled] = useState(() => isStandalone());
  const [promptable, setPromptable] = useState(() => canPromptInstall());
  const [outcome, setOutcome] = useState("");
  useEffect(() => onInstallPromptChange((v) => { setPromptable(v); if (!v && !canPromptInstall()) setInstalled(isStandalone()); }), []);
  useEffect(() => { document.title = `${t("install.title")} – ${t("app.name")}`; }, [t, lang]);

  const install = async () => { const r = await promptInstall(); setOutcome(r || ""); if (r === "accepted") setInstalled(true); };
  const kind = device.kind;
  const buttonPath = kind === "android" || kind === "desktop-chromium";

  return (
    <div className="min-h-screen bg-[#F7F4EC] px-4 py-6 dark:bg-slate-950" data-testid="install-page" data-device={kind}>
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icon-192-maskable.png" alt="" className="h-10 w-10 rounded-lg" />
            <div>
              <div className="text-lg font-semibold tracking-tight">{t("app.name")}</div>
              <div className="text-xs text-muted-foreground">{t("app.tagline")}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={toggle} data-testid="lang-toggle-auth"><Languages className="me-1 h-4 w-4" />{lang === "en" ? "EN" : "ع"}</Button>
        </div>
        <h1 className="text-2xl font-bold tracking-tight">{t("install.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("install.intro")}</p>

        <div className="mt-6 space-y-4">
          {installed && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="install-installed">
              <div className="flex items-center gap-2 font-medium"><Check className="h-4 w-4" />{t("install.already")}</div>
              <Button asChild className="mt-3 h-11 w-full"><Link to="/" data-testid="install-open"><ExternalLink className="me-2 h-4 w-4" />{t("install.open")}</Link></Button>
            </div>
          )}
          {!installed && buttonPath && (
            <div className="rounded-lg border bg-white p-4 dark:bg-slate-900" data-testid="install-button-card">
              <div className="text-sm">{kind === "android" ? t("install.androidLead") : t("install.desktopLead")}</div>
              {promptable ? (
                <Button className="mt-3 h-11 w-full" onClick={install} data-testid="install-button"><Download className="me-2 h-4 w-4" />{t("install.button")}</Button>
              ) : (
                <ol className="mt-3 space-y-2" data-testid="install-steps-chromium">
                  <Step n={1} icon={<MonitorDown className="h-6 w-6" />} text={kind === "android" ? t("install.androidStep1") : t("install.desktopStep1")} />
                  <Step n={2} icon={<Download className="h-6 w-6" />} text={kind === "android" ? t("install.androidStep2") : t("install.desktopStep2")} />
                </ol>
              )}
              {outcome === "dismissed" && <div className="mt-2 text-xs text-muted-foreground" data-testid="install-dismissed">{t("install.dismissed")}</div>}
            </div>
          )}
          {!installed && kind === "ios" && (
            <div className="rounded-lg border bg-white p-4 dark:bg-slate-900" data-testid="install-ios">
              <div className="text-sm">{device.browser === "safari" ? t("install.iosLead") : t("install.iosOpenSafari")}</div>
              <ol className="mt-3 space-y-2">
                <Step n={1} icon={<Share className="h-6 w-6" />} text={t("install.iosStep1")} />
                <Step n={2} icon={<PlusSquare className="h-6 w-6" />} text={t("install.iosStep2")} />
                <Step n={3} icon={<Check className="h-6 w-6" />} text={t("install.iosStep3")} />
              </ol>
            </div>
          )}
          {!installed && kind === "mac-safari" && (
            <div className="rounded-lg border bg-white p-4 dark:bg-slate-900" data-testid="install-mac">
              <div className="text-sm">{t("install.macLead")}</div>
              <ol className="mt-3 space-y-2">
                <Step n={1} icon={<Share className="h-6 w-6" />} text={t("install.macStep1")} />
                <Step n={2} icon={<PlusSquare className="h-6 w-6" />} text={t("install.macStep2")} />
                <Step n={3} icon={<Check className="h-6 w-6" />} text={t("install.macStep3")} />
              </ol>
            </div>
          )}
          {!installed && kind === "desktop-other" && (
            <div className="rounded-lg border bg-white p-4 text-sm dark:bg-slate-900" data-testid="install-other">{t("install.otherBrowser")}</div>
          )}
        </div>

        <div className="mt-8 text-center text-sm">
          <Link to="/login" className="font-medium text-emerald-700 hover:underline" data-testid="install-signin">{t("install.signIn")}</Link>
        </div>
      </div>
    </div>
  );
}
